import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import supabase from '../config/supabase';

const { FiLock, FiAlertCircle, FiCheckCircle, FiEye, FiEyeOff, FiKey } = FiIcons;

// ✅ 模組層級：靜態 variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};
const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, delay: 0.2 } },
};
const messageVariants = {
  hidden: { opacity: 0, y: -10 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 10 },
};

// ✅ 模組層級：InputField 純組件
const InputField = React.memo(({ id, type, value, onChange, placeholder, icon, children, isDark, isLoading }) => (
  <div className="relative">
    <SafeIcon
      icon={icon}
      className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
    />
    <input
      id={id} type={type} value={value} onChange={onChange}
      placeholder={placeholder}
      className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        isDark
          ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400'
          : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
      }`}
      required disabled={isLoading} autoComplete="new-password"
    />
    {children}
  </div>
));
InputField.displayName = 'InputField';

const ResetPasswordPage = () => {
  const [formData, setFormData] = useState({ password: '', confirmPassword: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState(false);
  const [isCheckingToken, setIsCheckingToken] = useState(true);
  const { navigateTo } = useLanguageNavigation();
  const { t } = useLanguage();
  const { isDark } = useTheme();

  useEffect(() => {
    const checkResetState = async () => {
      try {
        const isRecoveryMode = sessionStorage.getItem('password_recovery_mode') === 'true';
        const hashFragment = window.location.hash;
        const hashParams = new URLSearchParams(hashFragment.substring(hashFragment.indexOf('?')));
        let type = hashParams.get('type');
        let accessToken = hashParams.get('access_token');

        if (!accessToken || !type) {
          if (hashFragment.includes('access_token=')) {
            const fragmentParams = new URLSearchParams(hashFragment.substring(1));
            type = fragmentParams.get('type');
            accessToken = fragmentParams.get('access_token');
          }
        }

        if (isRecoveryMode || (type === 'recovery' && accessToken)) {
          if (accessToken) {
            const { error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || '',
            });
            if (sessionError) throw sessionError;
          }
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            setIsValidToken(true);
          } else {
            throw new Error('No active session');
          }
        } else {
          setError(t('auth.invalidResetLink'));
          setIsValidToken(false);
          setTimeout(() => navigateTo('/auth'), 3000);
        }
      } catch {
        setError(t('auth.resetLinkExpired'));
        setIsValidToken(false);
        setTimeout(() => navigateTo('/auth'), 3000);
      } finally {
        setIsCheckingToken(false);
      }
    };
    checkResetState();
  }, [t, navigateTo]);

  const handlePasswordChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, password: e.target.value }));
    setError(null);
  }, []);

  const handleConfirmPasswordChange = useCallback((e) => {
    setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
    setError(null);
  }, []);

  const toggleShowPassword = useCallback(() => setShowPassword(v => !v), []);

  const translateError = useCallback((msg) => {
    if (!msg) return t('auth.unexpectedError');
    const map = {
      'Password should be at least 6 characters': t('auth.passwordTooShort'),
      'New password should be different from the old password': t('auth.samePasswordError'),
    };
    if (map[msg]) return map[msg];
    for (const [key, val] of Object.entries(map)) {
      if (msg.includes(key)) return val;
    }
    return msg;
  }, [t]);

  const validateForm = useCallback(() => {
    const { password, confirmPassword } = formData;
    if (!password) return t('auth.passwordRequired');
    if (password.length < 6) return t('auth.passwordTooShort');
    if (!confirmPassword) return t('auth.confirmPasswordRequired');
    if (password !== confirmPassword) return t('auth.passwordMismatch');
    return null;
  }, [formData, t]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    const validationError = validateForm();
    if (validationError) { setError(validationError); return; }
    setIsLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: formData.password });
      if (updateError) throw new Error(translateError(updateError.message));
      setSuccess(t('auth.passwordUpdatedSuccess'));
      sessionStorage.removeItem('password_recovery_mode');
      window.history.replaceState({}, document.title, window.location.pathname + window.location.hash.split('?')[0]);
      setTimeout(() => navigateTo('/dashboard'), 2000);
    } catch (err) {
      setError(translateError(err.message));
    } finally {
      setIsLoading(false);
    }
  }, [formData.password, validateForm, translateError, t, navigateTo]);

  if (isCheckingToken) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('auth.verifyingResetLink')}</p>
        </div>
      </div>
    );
  }

  if (!isValidToken) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <motion.div variants={containerVariants} initial="hidden" animate="visible"
          className={`w-full max-w-md rounded-xl shadow-lg p-8 space-y-6 text-center ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <SafeIcon icon={FiAlertCircle} className="w-16 h-16 text-red-500 mx-auto" />
          <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('auth.invalidResetLink')}</h2>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{error || t('auth.resetLinkExpiredMessage')}</p>
          <button onClick={() => navigateTo('/auth')} className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors">
            {t('auth.backToSignIn')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 pt-24 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible"
        className={`w-full max-w-md rounded-xl shadow-lg p-8 space-y-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
        <motion.div variants={itemVariants} className="text-center">
          <div className={`inline-block p-3 rounded-full mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <SafeIcon icon={FiKey} className="w-8 h-8 text-blue-500" />
          </div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('auth.resetPasswordTitle')}</h1>
          <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('auth.resetPasswordSubtitle')}</p>
        </motion.div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div key="error" variants={messageVariants} initial="hidden" animate="visible" exit="exit"
              className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
              <SafeIcon icon={FiAlertCircle} className="mr-3 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
          {success && (
            <motion.div key="success" variants={messageVariants} initial="hidden" animate="visible" exit="exit"
              className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
              <SafeIcon icon={FiCheckCircle} className="mr-3 flex-shrink-0" />
              <span className="text-sm">{success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.form variants={itemVariants} onSubmit={handleSubmit} className="space-y-4">
          <InputField
            id="newPassword" type={showPassword ? 'text' : 'password'}
            value={formData.password} onChange={handlePasswordChange}
            placeholder={t('auth.newPasswordPlaceholder')}
            icon={FiLock} isDark={isDark} isLoading={isLoading}
          >
            <button type="button" onClick={toggleShowPassword}
              className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}>
              <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
            </button>
          </InputField>

          <InputField
            id="confirmNewPassword" type="password"
            value={formData.confirmPassword} onChange={handleConfirmPasswordChange}
            placeholder={t('auth.confirmNewPasswordPlaceholder')}
            icon={FiLock} isDark={isDark} isLoading={isLoading}
          />

          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit"
            className="w-full bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
            disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t('auth.updatingPassword')}
              </div>
            ) : t('auth.updatePasswordButton')}
          </motion.button>
        </motion.form>

        <motion.div variants={itemVariants} className="text-center">
          <button onClick={() => navigateTo('/auth')} disabled={isLoading}
            className={`text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>
            {t('auth.backToSignIn')}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ResetPasswordPage;